// theme.js
import { createTheme, alpha } from '@mui/material/styles';
import { DEFAULT_COLORS } from './utils';

/**
 * Creates a dynamic theme based on provided colors
 * If no colors are provided, falls back to DEFAULT_COLORS
 */
export function createAppTheme(appColors = null) {
  let colors = DEFAULT_COLORS;
  
  if (appColors) {
    if (appColors.colors && appColors.colors.palette) {
      // Extended SSO format (all tokens)
      const p = appColors.colors.palette;
      
      const primary = { ...DEFAULT_COLORS.primary, ...(p.primary || {}) };
      if (p.primary?.main) {
        if (!p.primary.light) primary.light = p.primary.main;
        if (!p.primary.dark)  primary.dark  = p.primary.main;
      }

      const secondary = { ...DEFAULT_COLORS.secondary, ...(p.secondary || {}) };
      if (p.secondary?.main) {
        if (!p.secondary.light) secondary.light = p.secondary.main;
        if (!p.secondary.dark)  secondary.dark  = p.secondary.main;
      }

      colors = {
        primary,
        secondary,
        text: { ...DEFAULT_COLORS.text, ...(p.text || {}) },
        others: {
          default: p.background?.default || appColors.backgroundColor || DEFAULT_COLORS.others.defaultColor,
          paper: p.background?.paper || DEFAULT_COLORS.others.paper
        }
      };
    } else if (appColors.primaryColor) {
      // Standard SSO branding format (simplified)
      colors = {
        primary: { main: appColors.primaryColor, light: appColors.primaryColor, dark: appColors.primaryColor, contrastText: '#FFF' },
        secondary: { main: appColors.secondaryColor, light: appColors.secondaryColor, dark: appColors.secondaryColor, contrastText: '#FFF' },
        text: DEFAULT_COLORS.text,
        others: { default: appColors.backgroundColor || DEFAULT_COLORS.others.defaultColor, paper: '#FFFFFF' }
      };
    } else {
      // Legacy local colors format
      colors = appColors;
    }
  }

  const safeAlpha = (color, value) => {
    try {
      if (!color || typeof color !== 'string') return 'rgba(0,0,0,0.05)';
      return alpha(color, value);
    } catch (e) {
      return 'rgba(0,0,0,0.05)';
    }
  };

  const PRIMARY   = colors.primary;
  const SECONDARY = colors.secondary;
  const TEXT      = colors.text;
  const BG        = {
    default: colors.others?.default || colors.others?.defaultColor || '#F8FAF7',
    paper:   colors.others?.paper || '#FFFFFF'
  };
  const DIVIDER   = 'rgba(0,0,0,0.10)'; // más lógico para modo claro

  // Tokens de superficies para tarjetas
  const surfaces = {
    card:        safeAlpha(PRIMARY.main,   0.06),
    cardHover:   safeAlpha(PRIMARY.main,   0.10),
    cardAlt:     safeAlpha(SECONDARY.main, 0.06),
    cardAltHover:safeAlpha(SECONDARY.main, 0.10),
    border:      safeAlpha(PRIMARY.main,   0.18),
    borderAlt:   safeAlpha(SECONDARY.main, 0.20),
    neutral:     '#F7F9FC',
    neutralHover:'#F1F4F9',
    neutralBorder:'#E6EDF7',
  };

  return createTheme({
    palette: {
      mode: 'light',
      primary: PRIMARY,
      secondary: SECONDARY,
      background: BG,
      text: TEXT,
      divider: DIVIDER,
      action: {
        active: '#000', // en light conviene base oscura
        hover: 'rgba(0,0,0,0.04)',
        selected: 'rgba(0,0,0,0.08)',
        disabled: 'rgba(0,0,0,0.26)',
        disabledBackground: 'rgba(0,0,0,0.12)',
      },
    },

    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      h1: { fontSize: '2.5rem', fontWeight: 500, color: TEXT.primary },
      h2: { fontSize: '2rem',   fontWeight: 500, color: TEXT.primary },
      h3: { fontSize: '1.75rem',fontWeight: 500, color: TEXT.primary },
      h4: { fontSize: '1.5rem', fontWeight: 500, color: TEXT.primary },
      h5: { fontSize: '1.25rem',fontWeight: 500, color: TEXT.primary },
      h6: { fontSize: '1rem',   fontWeight: 500, color: TEXT.primary },
      body1: { color: TEXT.primary },   // ✅ legible en modo claro
      body2: { color: TEXT.secondary },
      caption:{ color: TEXT.hint },
    },

    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 8,
            paddingInline: 16,
            '&.Mui-disabled': {
              color: `${TEXT.disabled} !important`,
              borderColor: `${safeAlpha('#000', 0.12)} !important`,
            },
          },
          // TEXT (sin fondo)
          text: {
            color: TEXT.secondary,
            '&:hover': { backgroundColor: safeAlpha('#000', 0.04) },
          },
          textPrimary: {
            color: PRIMARY.light,
            '&:hover': { backgroundColor: safeAlpha(PRIMARY.light, 0.12) },
          },
          textSecondary: {
            color: SECONDARY.light,
            '&:hover': { backgroundColor: safeAlpha(SECONDARY.light, 0.12) },
          },

          // OUTLINED
          outlined: {
            color: TEXT.secondary,
            borderColor: safeAlpha('#000', 0.12),
            '&:hover': {
              borderColor: safeAlpha('#000', 0.24),
              backgroundColor: safeAlpha('#000', 0.04),
            },
          },
          outlinedPrimary: {
            color: PRIMARY.light,
            borderColor: safeAlpha(PRIMARY.light, 0.5),
            '&:hover': {
              borderColor: safeAlpha(PRIMARY.light, 0.9),
              backgroundColor: safeAlpha(PRIMARY.light, 0.10),
            },
          },
          outlinedSecondary: {
            color: SECONDARY.light,
            borderColor: safeAlpha(SECONDARY.light, 0.5),
            '&:hover': {
              borderColor: safeAlpha(SECONDARY.light, 0.9),
              backgroundColor: safeAlpha(SECONDARY.light, 0.10),
            },
          },

          // CONTAINED
          contained: {
            '&:hover': { backgroundColor: PRIMARY.light },
          },
        },
      },

      MuiIconButton: {
        styleOverrides: {
          root: {
            color: TEXT.secondary,
            '&:hover': { backgroundColor: safeAlpha('#000', 0.04) },
          },
          colorPrimary: {
            color: PRIMARY.light,
            '&:hover': { backgroundColor: safeAlpha(PRIMARY.light, 0.16) },
          },
        },
      },

      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 8,
              backgroundColor: 'rgba(0,0,0,0.02)',
              '&:hover fieldset': { borderColor: PRIMARY.light },
              '&.Mui-focused fieldset': { borderColor: PRIMARY.light },
            },
            '& .MuiInputBase-input::placeholder': {
              color: TEXT.hint,
              opacity: 1,
            },
          },
        },
      },

      // ✅ Tarjetas con variantes "soft", "soft-secondary" y "neutral"
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            // No forzar BG global (antes usabas BG.paper y pintaba todo rosa)
          },
        },
        variants: [
          {
            props: { variant: 'soft' },
            style: {
              backgroundColor: surfaces.card,
              border: `1px solid ${surfaces.border}`,
              boxShadow: '0 1px 2px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.04)',
              transition: 'background-color .2s ease, box-shadow .2s ease',
              '&:hover': {
                backgroundColor: surfaces.cardHover,
                boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
              },
            },
          },
          {
            props: { variant: 'soft-secondary' },
            style: {
              backgroundColor: surfaces.cardAlt,
              border: `1px solid ${surfaces.borderAlt}`,
              boxShadow: '0 1px 2px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.04)',
              '&:hover': { backgroundColor: surfaces.cardAltHover },
            },
          },
          {
            props: { variant: 'neutral' },
            style: {
              backgroundColor: surfaces.neutral,
              border: `1px solid ${surfaces.neutralBorder}`,
              boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
              '&:hover': { backgroundColor: surfaces.neutralHover },
            },
          },
        ],
      },

      MuiCard: {
        styleOverrides: {
          root: { borderRadius: 12 },
        },
        variants: [
          { props: { variant: 'soft' },           style: { backgroundColor: surfaces.card,        border: `1px solid ${surfaces.border}` } },
          { props: { variant: 'soft-secondary' }, style: { backgroundColor: surfaces.cardAlt,     border: `1px solid ${surfaces.borderAlt}` } },
          { props: { variant: 'neutral' },        style: { backgroundColor: surfaces.neutral,     border: `1px solid ${surfaces.neutralBorder}` } },
        ],
      },
    },
  });
}

/**
 * Default static theme for backward compatibility
 * @deprecated Use createAppTheme instead
 */
export const theme = createAppTheme();

export default createAppTheme;
